import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DiscountAmountType,
  DiscountType,
  OwnerEvent,
  clubsBookingApi,
} from '@/api/clubs-booking';
import AdminShell from '@/components/admin/AdminShell';

type DiscountFormState = {
  name: string;
  type: DiscountType;
  amountType: DiscountAmountType;
  amountValue: string;
  code: string;
  minAttendees: string;
  minSessionsPerAttendee: string;
  minBookingTotal: string;
  appliesToAllActivities: boolean;
  activityIds: string[];
  startsAt: string;
  endsAt: string;
  enabled: boolean;
};

const emptyForm: DiscountFormState = {
  name: '',
  type: 'multi_attendee',
  amountType: 'percentage',
  amountValue: '10',
  code: '',
  minAttendees: '2',
  minSessionsPerAttendee: '3',
  minBookingTotal: '',
  appliesToAllActivities: true,
  activityIds: [],
  startsAt: '',
  endsAt: '',
  enabled: true,
};

const toDateTimeLocalInput = (isoDate?: string) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const fromDateTimeLocalInput = (value: string) => {
  if (!value.trim()) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

const DiscountCreatePage = () => {
  const navigate = useNavigate();
  const { discountId } = useParams<{ discountId: string }>();
  const isEditing = Boolean(discountId);

  const [events, setEvents] = useState<OwnerEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<DiscountFormState>(emptyForm);

  const getErrorMessage = useCallback((err: any, fallback: string) => {
    if (err?.response?.status === 404) {
      return 'Discounts API endpoint is unavailable. Restart the API so the new discounts routes are loaded.';
    }
    const messageText = err?.response?.data?.message;
    return Array.isArray(messageText) ? messageText.join(', ') : messageText || err?.message || fallback;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const eventData = await clubsBookingApi.listMyEvents();
      setEvents(eventData.items || []);

      if (discountId) {
        const discount = await clubsBookingApi.getMyDiscount(discountId);
        setForm({
          name: discount.name,
          type: discount.type,
          amountType: discount.amountType,
          amountValue:
            discount.amountType === 'fixed'
              ? (discount.amountValue / 100).toFixed(2)
              : String(discount.amountValue),
          code: discount.code || '',
          minAttendees: String(discount.minAttendees || 2),
          minSessionsPerAttendee: String(discount.minSessionsPerAttendee || 3),
          minBookingTotal:
            typeof discount.minBookingTotalCents === 'number'
              ? (discount.minBookingTotalCents / 100).toFixed(2)
              : '',
          appliesToAllActivities: discount.appliesToAllActivities,
          activityIds: discount.appliesToAllActivities ? [] : (discount.activityIds || []),
          startsAt: toDateTimeLocalInput(discount.startsAt),
          endsAt: toDateTimeLocalInput(discount.endsAt),
          enabled: discount.enabled,
        });
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load discount setup'));
    } finally {
      setLoading(false);
    }
  }, [discountId, getErrorMessage]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSave = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const amountNumeric = Number(form.amountValue);
      if (!Number.isFinite(amountNumeric) || amountNumeric <= 0) {
        throw new Error('Enter a valid discount amount.');
      }

      const payload = {
        name: form.name.trim(),
        type: form.type,
        amountType: form.amountType,
        amountValue:
          form.amountType === 'fixed'
            ? Math.round(amountNumeric * 100)
            : Math.round(amountNumeric),
        code: form.type === 'code' ? form.code.trim().toUpperCase() : undefined,
        minAttendees:
          form.type === 'multi_attendee'
            ? Math.max(2, Math.round(Number(form.minAttendees || 2)))
            : undefined,
        minSessionsPerAttendee:
          form.type === 'multi_purchase'
            ? Math.max(2, Math.round(Number(form.minSessionsPerAttendee || 3)))
            : undefined,
        minBookingTotalCents:
          form.type === 'code' && form.minBookingTotal.trim()
            ? Math.max(0, Math.round(Number(form.minBookingTotal) * 100))
            : undefined,
        appliesToAllActivities: form.appliesToAllActivities,
        activityIds: form.appliesToAllActivities ? [] : form.activityIds,
        startsAt: fromDateTimeLocalInput(form.startsAt),
        endsAt: fromDateTimeLocalInput(form.endsAt),
        enabled: form.enabled,
      };

      if (!payload.name) {
        throw new Error('Discount name is required.');
      }
      if (!payload.appliesToAllActivities && !payload.activityIds.length) {
        throw new Error('Choose at least one activity for this discount.');
      }
      if (payload.type === 'code' && !payload.code) {
        throw new Error('Code is required for discount code rules.');
      }

      if (discountId) {
        await clubsBookingApi.updateDiscount(discountId, payload);
      } else {
        await clubsBookingApi.createDiscount(payload);
      }

      navigate('/discounts');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Could not save discount'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title="Discounts" subtitle="Configure code and automatic discount rules">
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold">{isEditing ? 'Edit discount' : 'Create discount'}</h2>
          <Link to="/discounts" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
            Back to list
          </Link>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}
        {loading && <p className="mb-4 text-sm text-slate-500">Loading form...</p>}

        <form onSubmit={onSave} className="grid gap-4 md:grid-cols-2">
          <label className="text-sm text-slate-600 md:col-span-2">
            Name
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
          </label>

          <label className="text-sm text-slate-600">
            Type
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
              value={form.type}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  type: e.target.value as DiscountType,
                  amountType: prev.type === 'code' && e.target.value !== 'code' ? 'percentage' : prev.amountType,
                }))
              }
            >
              <option value="multi_attendee">Multi attendee rule</option>
              <option value="multi_purchase">Multi purchase rule</option>
              <option value="code">Discount code</option>
            </select>
          </label>

          <label className="text-sm text-slate-600">
            Amount type
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
              value={form.amountType}
              onChange={(e) => setForm((prev) => ({ ...prev, amountType: e.target.value as DiscountAmountType }))}
            >
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed amount (Â£)</option>
            </select>
          </label>

          <label className="text-sm text-slate-600">
            Amount
            <input
              type="number"
              min={0}
              step={form.amountType === 'fixed' ? '0.01' : '1'}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.amountValue}
              onChange={(e) => setForm((prev) => ({ ...prev, amountValue: e.target.value }))}
              required
            />
          </label>

          {form.type === 'code' && (
            <label className="text-sm text-slate-600">
              Code
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 uppercase"
                value={form.code}
                onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                required
              />
            </label>
          )}

          {form.type === 'multi_attendee' && (
            <label className="text-sm text-slate-600">
              Minimum attendees
              <input
                type="number"
                min={2}
                step={1}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={form.minAttendees}
                onChange={(e) => setForm((prev) => ({ ...prev, minAttendees: e.target.value }))}
                required
              />
            </label>
          )}

          {form.type === 'multi_purchase' && (
            <label className="text-sm text-slate-600">
              Minimum sessions per attendee
              <input
                type="number"
                min={2}
                step={1}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={form.minSessionsPerAttendee}
                onChange={(e) => setForm((prev) => ({ ...prev, minSessionsPerAttendee: e.target.value }))}
                required
              />
            </label>
          )}

          {form.type === 'code' && (
            <label className="text-sm text-slate-600">
              Minimum booking total (optional)
              <input
                type="number"
                min={0}
                step="0.01"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={form.minBookingTotal}
                onChange={(e) => setForm((prev) => ({ ...prev, minBookingTotal: e.target.value }))}
              />
            </label>
          )}

          <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-medium text-slate-700">Activities this discount applies to</p>
            <div className="mt-2 flex flex-wrap gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  checked={form.appliesToAllActivities}
                  onChange={() => setForm((prev) => ({ ...prev, appliesToAllActivities: true, activityIds: [] }))}
                />
                All activities
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  checked={!form.appliesToAllActivities}
                  onChange={() => setForm((prev) => ({ ...prev, appliesToAllActivities: false }))}
                />
                Selected activities
              </label>
            </div>
            {!form.appliesToAllActivities && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {events.map((entry) => (
                  <label key={entry._id} className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={form.activityIds.includes(entry._id)}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          activityIds: e.target.checked
                            ? Array.from(new Set([...prev.activityIds, entry._id]))
                            : prev.activityIds.filter((id) => id !== entry._id),
                        }))
                      }
                    />
                    <span>{entry.title}</span>
                  </label>
                ))}
                {events.length === 0 && <p className="text-xs text-slate-500">Create an activity first.</p>}
              </div>
            )}
          </div>

          <label className="text-sm text-slate-600">
            Starts at (optional)
            <input
              type="datetime-local"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.startsAt}
              onChange={(e) => setForm((prev) => ({ ...prev, startsAt: e.target.value }))}
            />
          </label>

          <label className="text-sm text-slate-600">
            Ends at (optional)
            <input
              type="datetime-local"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.endsAt}
              onChange={(e) => setForm((prev) => ({ ...prev, endsAt: e.target.value }))}
            />
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 md:col-span-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            Enabled
          </label>

          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              className="rounded-lg bg-[#1F8F5F] px-4 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60"
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : isEditing ? 'Save changes' : 'Create discount'}
            </button>
          </div>
        </form>
      </section>
    </AdminShell>
  );
};

export default DiscountCreatePage;


